import { Card, Flex, Tooltip, Typography } from "antd";
import { ArrowDownOutlined, ArrowUpOutlined, InfoCircleOutlined, ThunderboltOutlined } from "@ant-design/icons";
import { TinyArea } from "@ant-design/plots";
import type { DashboardPeriod } from "../../utils";

const { Text } = Typography;

const CARD_HEAD   = 38;
const PAD_V       = 24;
const NUMBER_H    = 52;
const LABEL_H     = 20;
const GAP_H       = 10;

type PeriodEntry = { avg: number; prev: number; trend: number[]; trendLabel: string };

// TODO: datos mock — reemplazar con llamada a la API cuando estén los endpoints del dashboard.
const PERIOD_DATA: Record<string, PeriodEntry> = {
  day:        { avg: 135, prev: 162, trend: [210, 180, 165, 190, 150, 120, 135], trendLabel: "Últimas 7 horas (min)"   },
  week:       { avg: 142, prev: 135, trend: [135, 150, 148, 142, 138, 145, 142], trendLabel: "Últimos 7 días (min)"    },
  month:      { avg: 128, prev: 145, trend: [145, 140, 138, 135, 130, 128, 128], trendLabel: "Últimas 7 semanas (min)" },
  prev_month: { avg: 145, prev: 162, trend: [162, 158, 152, 148, 145, 147, 145], trendLabel: "Últimas 7 semanas (min)" },
};

const fmtMin = (min: number) => {
  const h = Math.floor(min / 60);
  const m = min % 60;
  return h > 0 ? `${h}h${m > 0 ? ` ${m}m` : ""}` : `${m}m`;
};

export const TiempoRespuestaChip = ({ height = 240, period = "day" }: { height?: number; period?: DashboardPeriod }) => {
  const { avg, prev, trend, trendLabel } = PERIOD_DATA[period] ?? PERIOD_DATA.day;
  const bodyH  = height - CARD_HEAD;
  const chartH = Math.max(30, bodyH - PAD_V - NUMBER_H - GAP_H - LABEL_H);

  const diff  = avg - prev;
  const mejor = diff < 0;
  const color = mejor ? "#15744b" : "#cf1322";
  const Icon  = mejor ? ArrowDownOutlined : ArrowUpOutlined;
  const pct   = Math.round(Math.abs(diff / prev) * 100);

  return (
    <Card
      size="small"
      style={{ height }}
      styles={{ body: { padding: "12px 16px", height: bodyH, overflow: "hidden", display: "flex", flexDirection: "column" } }}
      title={
        <Flex align="center" gap={8}>
          <ThunderboltOutlined style={{ fontSize: 14 }} />
          <span style={{ fontWeight: 600 }}>Tiempo de respuesta</span>
          <Tooltip
            title="Minutos que tarda el equipo en contestar al cliente. Una respuesta rápida mantiene el interés del comprador y mejora las chances de cierre."
            trigger="click"
            color="#fff"
            overlayInnerStyle={{ color: "rgba(0,0,0,0.85)", boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }}
            overlayStyle={{ maxWidth: 240 }}
          >
            <InfoCircleOutlined style={{ fontSize: 12, color: "rgba(0,0,0,0.3)", cursor: "pointer" }} onClick={(e) => e.stopPropagation()} />
          </Tooltip>
        </Flex>
      }
      extra={
        <Text type="secondary" style={{ fontSize: 11 }}>
          {({ day: "Hoy", week: "Esta semana", month: "Este mes", prev_month: "Mes anterior" } as Record<string, string>)[period] ?? period}
        </Text>
      }
    >
      {/* Big metric */}
      <div style={{ flexShrink: 0 }}>
        <Flex align="baseline" gap={8} style={{ marginBottom: 2 }}>
          <Text strong style={{ fontSize: 32, lineHeight: 1 }}>{fmtMin(avg)}</Text>
          <span style={{ fontSize: 11, color, fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 2 }}>
            <Icon style={{ fontSize: 9 }} />
            {mejor ? "-" : "+"}{pct}% vs ant.
          </span>
        </Flex>
        <Text type="secondary" style={{ fontSize: 11 }}>
          Promedio · anterior {fmtMin(prev)}
        </Text>
      </div>

      {/* Sparkline */}
      <div style={{ marginTop: GAP_H, flexShrink: 0 }}>
        <Text style={{ fontSize: 10, color: "rgba(0,0,0,0.3)", display: "block", marginBottom: 4 }}>
          {trendLabel}
        </Text>
        <div style={{ height: chartH, overflow: "hidden" }}>
          <TinyArea
            data={trend}
            autoFit
            smooth
            color="#1677ff"
            areaStyle={{ fillOpacity: 0.12 }}
            line={{ color: "#1677ff", size: 1.5 }}
            tooltip={false}
            animation={false}
          />
        </div>
      </div>
    </Card>
  );
};
